import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import './Admin.css';
import { PackageIcon, UsersIcon, TruckIcon, CopyIcon } from '../../components/SvgIcons';
import AedPrice from '../../components/AedPrice';

const EMIRATES = [
  { id: 'abu_dhabi', name: 'Abu Dhabi', nameAr: 'أبوظبي' },
  { id: 'al_ain', name: 'Al Ain', nameAr: 'العين' },
  { id: 'dubai', name: 'Dubai', nameAr: 'دبي' },
  { id: 'sharjah', name: 'Sharjah', nameAr: 'الشارقة' },
  { id: 'ajman', name: 'Ajman', nameAr: 'عجمان' },
  { id: 'umm_al_quwain', name: 'Umm Al Quwain', nameAr: 'أم القيوين' },
  { id: 'ras_al_khaimah', name: 'Ras Al Khaimah', nameAr: 'رأس الخيمة' },
  { id: 'fujairah', name: 'Fujairah', nameAr: 'الفجيرة' }
];

const CreateOrder = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { t, language, isRTL } = useLanguage();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [createdOrder, setCreatedOrder] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  const [orderItems, setOrderItems] = useState([{ productId: '', quantity: 1 }]);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    whatsappName: ''
  });
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [deliveryFeeOptions, setDeliveryFeeOptions] = useState([]);
  const [payToDriver, setPayToDriver] = useState(false);
  const [whatsappTemplate, setWhatsappTemplate] = useState('');
  const [whatsappDriverTemplate, setWhatsappDriverTemplate] = useState('');
  const [phoneOrders, setPhoneOrders] = useState([]);
  const [phoneLoading, setPhoneLoading] = useState(false);
  const phoneDebounceRef = useRef(null);

  const [shippingAddress, setShippingAddress] = useState({
    fullName: '',
    address: '',
    city: '',
    emirate: '',
    phone: '',
    country: 'UAE'
  });

  useEffect(() => {
    fetchProducts();
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch('/api/settings', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const settings = await response.json();
        // Load delivery fee options
        if (settings.deliveryFeeOptions) {
          try {
            const opts = JSON.parse(settings.deliveryFeeOptions);
            if (Array.isArray(opts) && opts.length > 0) {
              const initFee = Number(opts[0]);
              setDeliveryFeeOptions(opts.map(Number));
              setDeliveryFee(initFee);
              if (initFee === 1) setPayToDriver(true);
            } else {
              const fee = Number(settings.deliveryFee) || 30;
              setDeliveryFeeOptions([fee]);
              setDeliveryFee(fee);
              if (fee === 1) setPayToDriver(true);
            }
          } catch(e) {
            const fee = Number(settings.deliveryFee) || 30;
            setDeliveryFeeOptions([fee]);
            setDeliveryFee(fee);
            if (fee === 1) setPayToDriver(true);
          }
        } else {
          const fee = Number(settings.deliveryFee) || 30;
          setDeliveryFeeOptions([fee]);
          setDeliveryFee(fee);
          if (fee === 1) setPayToDriver(true);
        }
        if (settings.whatsappTemplate) setWhatsappTemplate(settings.whatsappTemplate);
        if (settings.whatsappDriverTemplate) setWhatsappDriverTemplate(settings.whatsappDriverTemplate);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const fetchOrdersByPhone = useCallback(async (phone) => {
    // Need at least 7 digits to search
    const digits = phone.replace(/[^0-9]/g, '');
    if (digits.length < 7) {
      setPhoneOrders([]);
      return;
    }
    setPhoneLoading(true);
    try {
      const response = await fetch(`/api/orders/by-phone/${encodeURIComponent(phone)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPhoneOrders(data);
      } else {
        setPhoneOrders([]);
      }
    } catch (error) {
      console.error('Failed to fetch orders by phone:', error);
      setPhoneOrders([]);
    } finally {
      setPhoneLoading(false);
    }
  }, [token]);

  const fetchProducts = async () => {
    try {
      const response = await fetch('/api/products');
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    setOrderItems([...orderItems, { productId: '', quantity: 1 }]);
  };

  const handleRemoveItem = (index) => {
    if (orderItems.length > 1) {
      setOrderItems(orderItems.filter((_, i) => i !== index));
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...orderItems];
    newItems[index][field] = field === 'quantity' ? parseInt(value) || 1 : value;
    setOrderItems(newItems);
  };

  const handleCustomerChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({ ...prev, [name]: value }));
    if (name === 'name') {
      setShippingAddress(prev => ({ ...prev, fullName: value }));
    }
    if (name === 'phone') {
      setShippingAddress(prev => ({ ...prev, phone: value }));
      // Debounced phone lookup
      if (phoneDebounceRef.current) clearTimeout(phoneDebounceRef.current);
      phoneDebounceRef.current = setTimeout(() => {
        fetchOrdersByPhone(value);
      }, 500);
    }
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setShippingAddress(prev => ({ ...prev, [name]: value }));
  };

  const calculateSubtotal = () => {
    return orderItems.reduce((total, item) => {
      const product = products.find(p => p.id === item.productId);
      if (product) {
        return total + (getDisplayPrice(product) * item.quantity);
      }
      return total;
    }, 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + (payToDriver ? 0 : deliveryFee);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    const validItems = orderItems.filter(item => item.productId && item.quantity > 0);
    if (validItems.length === 0) {
      setError('Please add at least one product');
      setSubmitting(false);
      return;
    }

    if (!shippingAddress.emirate) {
      setError('Please select an Emirate / City');
      setSubmitting(false);
      return;
    }

    try {
      const response = await fetch('/api/orders/admin/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          items: validItems,
          customerName: customerInfo.name || null,
          customerPhone: customerInfo.phone || null,
          customerEmail: customerInfo.email || null,
          whatsappName: customerInfo.whatsappName || null,
          shippingAddress: (shippingAddress.address || shippingAddress.emirate) ? shippingAddress : null,
          deliveryFee: deliveryFee,
          payToDriver: payToDriver
        })
      });

      const data = await response.json();

      if (response.ok) {
        setCreatedOrder(data);
      } else {
        setError(data.error || data.message || `Failed to create order (${response.status})`);
      }
    } catch (err) {
      setError(`Failed to create order: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const getPaymentLink = () => {
    if (!createdOrder) return '';
    const orderId = createdOrder.order ? createdOrder.order.id : createdOrder.id;
    return createdOrder.paymentLink || `${window.location.origin}/pay/${orderId}`;
  };

  const getOrderId = () => {
    if (!createdOrder) return '';
    return createdOrder.order ? createdOrder.order.id : createdOrder.id;
  };

  const copyLink = () => {
    navigator.clipboard.writeText(getPaymentLink());
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const formatPhoneForWhatsApp = (phoneNumber) => {
    // Remove all non-digit characters
    let phone = phoneNumber.replace(/[^0-9]/g, '');

    // UAE local numbers starting with 05x (050, 052, 054, 055, 056, 058)
    if (phone.startsWith('05') && phone.length === 10) {
      // Convert 05xxxxxxxx to 9715xxxxxxxx
      phone = '971' + phone.substring(1);
    }
    // If already has country code 971
    else if (phone.startsWith('971')) {
      // Already correct format
    }
    // If starts with 00971
    else if (phone.startsWith('00971')) {
      phone = phone.substring(2);
    }
    // If starts with +971 (already stripped to 971)

    return phone;
  };

  const openWhatsApp = () => {
    const phone = formatPhoneForWhatsApp(customerInfo.phone);
    const total = calculateTotal();
    const defaultTemplate = `السلام عليكم،\n\nشكراً لطلبكم المنتجات الخالية من الغلوتين\n\nالاسم: {customerName}\nرقم الطلب: {orderId}\nالمبلغ الإجمالي: {total} درهم شامل التوصيل.\n\nيرجى إكمال الطلب عبر الرابط التالي:\n{paymentLink}`;
    const defaultDriverTemplate = `السلام عليكم،\n\nشكراً لطلبكم المنتجات الخالية من الغلوتين\n\nالاسم: {customerName}\nرقم الطلب: {orderId}\nالمبلغ الإجمالي: {total} درهم\n\nرسوم التوصيل تُحدد حسب المنطقة وتُدفع نقداً للسائق عند الاستلام\nDelivery fee is based on your area and paid cash to the driver\n\nيرجى إكمال الطلب عبر الرابط التالي:\n{paymentLink}`;
    const isDriverOrder = payToDriver && deliveryFee > 0;
    const template = isDriverOrder
      ? (whatsappDriverTemplate || defaultDriverTemplate)
      : (whatsappTemplate || defaultTemplate);
    const message = template
      .replace(/\{customerName\}/g, customerInfo.name || '')
      .replace(/\{orderId\}/g, getOrderId())
      .replace(/\{total\}/g, total.toFixed(0))
      .replace(/\{deliveryFee\}/g, String(Math.round(deliveryFee)))
      .replace(/\{paymentLink\}/g, getPaymentLink());
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const getProductName = (product) => {
    return language === 'ar' && product.nameAr ? product.nameAr : product.name;
  };

  const getDisplayPrice = (product) => {
    const price = product.onSale && product.salePrice ? product.salePrice : product.price;
    return Number(price) || 0;
  };

  const getSelectedProduct = (productId) => {
    return products.find(p => p.id === productId);
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (createdOrder) {
    return (
      <div className={`admin-page ${isRTL ? 'rtl' : ''}`}>
        <div className="container">
          <div className="admin-card" style={{ textAlign: 'center', padding: '40px 20px', marginTop: '20px' }}>
            <div style={{ marginBottom: '20px' }}><svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg></div>
            <h2 style={{ marginBottom: '10px' }}>
              {language === 'ar' ? 'تم إنشاء الطلب بنجاح!' : 'Order Created Successfully!'}
            </h2>
            <p style={{ color: 'var(--gray-600)', marginBottom: '30px' }}>
              {language === 'ar' ? 'رقم الطلب:' : 'Order ID:'} <strong>{getOrderId()}</strong>
            </p>

            <div style={{
              background: 'var(--gray-100)',
              padding: '20px',
              borderRadius: '12px',
              marginBottom: '30px'
            }}>
              <p style={{ marginBottom: '10px', fontWeight: '600' }}>
                {language === 'ar' ? 'رابط الدفع للعميل:' : 'Payment Link for Customer:'}
              </p>
              <div style={{
                background: 'white',
                padding: '15px',
                borderRadius: '8px',
                border: '1px solid var(--gray-300)',
                wordBreak: 'break-all',
                marginBottom: '15px',
                fontSize: '14px'
              }}>
                {getPaymentLink()}
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button
                  className="btn btn-primary"
                  onClick={copyLink}
                  style={{ minWidth: '150px' }}
                >
                  {linkCopied ? (language === 'ar' ? 'تم النسخ!' : 'Copied!') : (language === 'ar' ? 'نسخ الرابط' : 'Copy Link')}
                </button>
                {customerInfo.phone && (
                  <button
                    type="button"
                    onClick={openWhatsApp}
                    className="btn"
                    style={{
                      background: '#25d366',
                      color: 'white',
                      minWidth: '150px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    WhatsApp
                  </button>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/dashboard4/orders" className="btn btn-secondary">
                {language === 'ar' ? 'عرض الطلبات' : 'View Orders'}
              </Link>
              <button
                className="btn btn-primary"
                onClick={() => { window.scrollTo(0, 0);
                  setCreatedOrder(null);
                  setOrderItems([{ productId: '', quantity: 1 }]);
                  setCustomerInfo({ name: '', email: '', phone: '' });
                  setPhoneOrders([]);
                  setShippingAddress({
                    fullName: '',
                    address: '',
                    city: '',
                    emirate: '',
                    phone: '',
                    country: 'UAE'
                  });
                }}
              >
                {language === 'ar' ? 'إنشاء طلب جديد' : 'Create Order'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`admin-page ${isRTL ? 'rtl' : ''}`}>
      <div className="container">
        <div className="admin-card" style={{ padding: '12px 16px', marginBottom: '24px', ...(window.innerWidth >= 768 ? { position: 'sticky', top: '72px', zIndex: 99 } : {}) }}>
          <div className="admin-nav" style={{ marginBottom: 0 }}>
            <Link to="/dashboard4">{language === 'ar' ? 'لوحة التحكم' : 'Dashboard'}</Link>
            <Link to="/dashboard4/products">{t('products')}</Link>
            <Link to="/dashboard4/orders">{t('orders')}</Link>
            <Link to="/dashboard4/create-order" className="active">{language === "ar" ? "إنشاء طلب" : "Create Order"}</Link>
            <Link to="/dashboard4/inventory">{language === "ar" ? "المخزون" : "Inventory"}</Link>
            <Link to="/dashboard4/stats">{language === "ar" ? "الإحصائيات" : "Stats"}</Link>
            <Link to="/dashboard4/settings">{language === "ar" ? "الإعدادات" : "Settings"}</Link>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="create-order-grid">
            <div>
              {/* Products Section */}
              <div className="admin-card">
                <div className="card-header">
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><PackageIcon size="18px" /> {language === 'ar' ? 'المنتجات' : 'Products'}</h3>
                </div>

                {orderItems.map((item, index) => {
                  const selectedProduct = getSelectedProduct(item.productId);
                  return (
                    <div key={index} className="product-item-card">
                      {/* Product Dropdown - only show if no product selected */}
                      {!selectedProduct && (
                        <select
                          className="form-input product-select"
                          value={item.productId}
                          onChange={(e) => handleItemChange(index, 'productId', e.target.value)}
                        >
                          <option value="">{language === 'ar' ? '— اختر المنتج —' : '— Select Product —'}</option>
                          {products.filter(product => !orderItems.some(oi => oi.productId === product.id.toString() && oi.productId !== item.productId)).map(product => (
                            <option key={product.id} value={product.id}>
                              {getProductName(product)} — {product.onSale && product.salePrice ? `${product.salePrice.toFixed(2)} (was ${product.price.toFixed(2)})` : product.price.toFixed(2)}
                            </option>
                          ))}
                        </select>
                      )}

                      {/* Selected Product Card */}
                      {selectedProduct && (
                        <div className="selected-product-card">
                          <div className="selected-product-top">
                            <img
                              src={selectedProduct.image}
                              alt={selectedProduct.name}
                              className="selected-product-img"
                            />
                            <div className="selected-product-info">
                              <div className="selected-product-name">{getProductName(selectedProduct)}</div>
                              {language === 'ar' && selectedProduct.name && (
                                <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>{selectedProduct.name}</div>
                              )}
                              {language !== 'ar' && selectedProduct.nameAr && (
                                <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>{selectedProduct.nameAr}</div>
                              )}
                            </div>
                            <div className="selected-product-price">
                              {selectedProduct.onSale && selectedProduct.salePrice ? (
                                <div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <AedPrice amount={selectedProduct.salePrice} />
                                    <span style={{ fontWeight: '700', color: '#dc2626', fontSize: '13px' }}>
                                      -{Math.round((1 - selectedProduct.salePrice / selectedProduct.price) * 100)}%
                                    </span>
                                  </div>
                                  <div style={{ fontSize: '12px', color: '#9ca3af', textDecoration: 'line-through', marginTop: '2px' }}>
                                    <AedPrice amount={selectedProduct.price} />
                                  </div>
                                </div>
                              ) : (
                                <AedPrice amount={selectedProduct.price} />
                              )}
                            </div>
                          </div>
                          <div className="selected-product-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div className="qty-controls">
                              <button
                                type="button"
                                className="qty-btn"
                                onClick={() => handleItemChange(index, 'quantity', Math.max(1, item.quantity - 1))}
                              >−</button>
                              <span className="qty-value" style={{ fontSize: '18px', fontWeight: '700', minWidth: '36px' }}>{item.quantity}</span>
                              <button
                                type="button"
                                className="qty-btn"
                                onClick={() => handleItemChange(index, 'quantity', item.quantity + 1)}
                              >+</button>
                            </div>
                            <button
                              type="button"
                              className="delete-btn"
                              onClick={() => handleRemoveItem(index)}
                              title="Remove"
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z"/>
                              </svg>
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Remove button when no product selected */}
                      {!selectedProduct && orderItems.length > 1 && (
                        <button
                          type="button"
                          className="remove-empty-btn"
                          onClick={() => handleRemoveItem(index)}
                        >{language === 'ar' ? 'إزالة' : 'Remove'}</button>
                      )}
                    </div>
                  );
                })}

                <button
                  type="button"
                  className="add-product-btn"
                  onClick={handleAddItem}
                >
                  + {language === 'ar' ? 'منتج آخر' : 'Add Product'}
                </button>
              </div>

              {/* Customer Info */}
              <div className="admin-card" style={{ marginTop: '20px' }}>
                <div className="card-header">
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><UsersIcon size="18px" /> {language === 'ar' ? 'معلومات العميل' : 'Customer Info'}</h3>
                  <small style={{ color: 'var(--gray-500)', fontWeight: 'normal' }}>
                    {language === 'ar' ? '(اختياري)' : '(Optional)'}
                  </small>
                </div>

                <div className="form-group">
                  <label className="form-label">{language === 'ar' ? 'الاسم' : 'Name'}</label>
                  <input
                    type="text"
                    name="name"
                    className="form-input"
                    value={customerInfo.name}
                    onChange={handleCustomerChange}
                    placeholder={language === 'ar' ? 'اسم العميل' : 'Customer name'}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    {language === 'ar' ? 'اسم الواتساب' : 'WhatsApp Name'}
                    <span style={{ color: '#9ca3af', fontWeight: 'normal', fontSize: '12px', marginLeft: '4px' }}>(Optional)</span>
                  </label>
                  <input
                    type="text"
                    name="whatsappName"
                    className="form-input"
                    value={customerInfo.whatsappName}
                    onChange={handleCustomerChange}
                    placeholder={language === 'ar' ? 'الاسم في الواتساب' : 'Name on WhatsApp'}
                    style={{ fontSize: '14px' }}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">{language === 'ar' ? 'البريد الإلكتروني' : 'Email'}</label>
                  <input
                    type="email"
                    name="email"
                    className="form-input"
                    value={customerInfo.email}
                    onChange={handleCustomerChange}
                    placeholder={language === 'ar' ? 'البريد الإلكتروني' : 'customer@email.com'}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">{language === 'ar' ? 'الهاتف' : 'Phone'}</label>
                  <input
                    type="tel"
                    name="phone"
                    className="form-input"
                    value={customerInfo.phone}
                    onChange={handleCustomerChange}
                    placeholder="05XXXXXXXX"
                  />

                  {/* Customer order history */}
                  {phoneLoading && (
                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#9ca3af' }}>
                      {language === 'ar' ? 'جاري البحث...' : 'Searching...'}
                    </div>
                  )}
                  {!phoneLoading && phoneOrders.length > 0 && (
                    <div style={{
                      marginTop: '10px',
                      padding: '12px',
                      background: '#f0f9ff',
                      border: '1px solid #bae6fd',
                      borderRadius: '10px'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '8px'
                      }}>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#0369a1' }}>
                          <PackageIcon size="14px" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '4px' }} />
                          {phoneOrders.length} {language === 'ar'
                            ? (phoneOrders.length === 1 ? 'طلب سابق' : 'طلبات سابقة')
                            : (phoneOrders.length === 1 ? 'previous order' : 'previous orders')}
                        </span>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: '#0369a1' }}>
                          <AedPrice amount={phoneOrders.reduce((sum, o) => sum + (o.total || 0), 0)} />
                        </span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {phoneOrders.slice(0, 5).map(o => (
                          <Link
                            key={o.id}
                            to={`/dashboard4/orders/${o.id}`}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '6px 8px',
                              background: 'white',
                              borderRadius: '6px',
                              textDecoration: 'none',
                              fontSize: '12px',
                              color: '#374151'
                            }}
                          >
                            <span style={{ fontFamily: 'monospace', fontWeight: '600' }}>{o.id}</span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span><AedPrice amount={o.total} /></span>
                              <span style={{
                                padding: '2px 8px',
                                borderRadius: '10px',
                                fontSize: '10px',
                                fontWeight: '600',
                                color: 'white',
                                background: o.status === 'delivered' ? '#10b981'
                                  : o.status === 'shipped' ? '#8b5cf6'
                                  : o.status === 'cancelled' ? '#ef4444'
                                  : o.status === 'processing' ? '#3b82f6'
                                  : '#f59e0b'
                              }}>
                                {t(o.status)}
                              </span>
                            </span>
                          </Link>
                        ))}
                        {phoneOrders.length > 5 && (
                          <div style={{ fontSize: '11px', color: '#6b7280', textAlign: 'center', marginTop: '4px' }}>
                            +{phoneOrders.length - 5} {language === 'ar' ? 'طلبات أخرى' : 'more orders'}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - Shipping & Summary */}
            <div>
              {/* Shipping Address */}
              <div className="admin-card">
                <div className="card-header">
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><TruckIcon size="18px" /> {language === 'ar' ? 'عنوان التوصيل' : 'Shipping Address'}</h3>
                  <small style={{ color: 'var(--gray-500)', fontWeight: 'normal' }}>
                    {language === 'ar' ? '(اختياري)' : '(Optional)'}
                  </small>
                </div>

                <div className="form-group">
                  <label className="form-label">{language === 'ar' ? 'الاسم الكامل' : 'Full Name'}</label>
                  <input
                    type="text"
                    name="fullName"
                    className="form-input"
                    value={shippingAddress.fullName}
                    onChange={handleAddressChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">{language === 'ar' ? 'العنوان' : 'Address'}</label>
                  <input
                    type="text"
                    name="address"
                    className="form-input"
                    value={shippingAddress.address}
                    onChange={handleAddressChange}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label className="form-label">{language === 'ar' ? 'المدينة' : 'City'}</label>
                    <input
                      type="text"
                      name="city"
                      className="form-input"
                      value={shippingAddress.city}
                      onChange={handleAddressChange}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">{language === 'ar' ? 'الإمارة / المدينة' : 'Emirate / City'} <span style={{ color: '#ef4444' }}>*</span></label>
                    <select
                      name="emirate"
                      className="form-input"
                      value={shippingAddress.emirate}
                      onChange={handleAddressChange}
                      required
                    >
                      <option value="">{language === 'ar' ? 'اختر الإمارة' : 'Select'}</option>
                      {EMIRATES.map(emirate => (
                        <option key={emirate.id} value={emirate.id}>
                          {language === 'ar' ? emirate.nameAr : emirate.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Order Summary */}
              <div className="admin-card" style={{ marginTop: '20px' }}>
                <div className="card-header">
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><CopyIcon size="18px" /> {language === 'ar' ? 'ملخص الطلب' : 'Order Summary'}</h3>
                </div>

                <div style={{ padding: '10px 0' }}>
                  {orderItems.filter(item => item.productId).map((item, index) => {
                    const product = products.find(p => p.id === item.productId);
                    if (!product) return null;
                    return (
                      <div key={index} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '10px',
                        color: 'var(--gray-600)'
                      }}>
                        <span>{getProductName(product)} x{item.quantity}</span>
                        <span><AedPrice amount={getDisplayPrice(product) * item.quantity} /></span>
                      </div>
                    );
                  })}

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: '15px',
                    paddingTop: '15px',
                    borderTop: '1px solid var(--gray-200)',
                    color: 'var(--gray-600)'
                  }}>
                    <span>{language === 'ar' ? 'المجموع الفرعي' : 'Subtotal'}</span>
                    <span><AedPrice amount={calculateSubtotal()} /></span>
                  </div>

                  <div style={{
                    marginTop: '12px',
                    padding: '12px 14px',
                    borderRadius: '12px',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'var(--gray-600)', marginBottom: (deliveryFeeOptions.length > 1 || deliveryFee > 0) ? '10px' : '0' }}>
                      <span style={{ fontWeight: '600', fontSize: '14px' }}>{language === 'ar' ? 'التوصيل' : 'Shipping'}</span>
                      {payToDriver && deliveryFee > 0 ? (
                        <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '10px', background: '#fffbeb', color: '#b45309', fontWeight: '600', border: '1px solid #fcd34d' }}>
                          🚚 {deliveryFee === 1 ? 'Pay to Driver (based on area)' : `Pay to Driver AED ${Math.round(deliveryFee)}`}
                        </span>
                      ) : (
                        <span style={{ fontWeight: '700', color: deliveryFee === 0 ? '#22c55e' : 'var(--gray-700)' }}>
                          {deliveryFee === 0 ? 'Free' : <AedPrice amount={deliveryFee} />}
                        </span>
                      )}
                    </div>
                    {deliveryFeeOptions.length > 1 && (
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: deliveryFee > 0 ? '10px' : '0' }}>
                        {deliveryFeeOptions.map((fee, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => { setDeliveryFee(fee); if (fee === 0) setPayToDriver(false); if (fee === 1) setPayToDriver(true); }}
                            style={{
                              padding: '6px 14px',
                              borderRadius: '16px',
                              border: deliveryFee === fee ? '2px solid var(--primary)' : '1px solid #d1d5db',
                              background: deliveryFee === fee ? '#eff6ff' : 'white',
                              color: deliveryFee === fee ? 'var(--primary)' : '#64748b',
                              fontWeight: deliveryFee === fee ? '700' : '500',
                              fontSize: '13px',
                              cursor: 'pointer',
                              transition: 'all 0.15s'
                            }}
                          >
                            {fee === 0 ? 'Free' : fee === 1 ? '🚚 Pay to Driver' : `AED ${fee}`}
                          </button>
                        ))}
                      </div>
                    )}
                    {deliveryFee > 1 && (
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer',
                        padding: '8px 10px',
                        borderRadius: '10px',
                        background: payToDriver ? '#fffbeb' : 'transparent',
                        border: payToDriver ? '1px solid #fcd34d' : '1px solid transparent',
                        transition: 'all 0.15s'
                      }}>
                        <input
                          type="checkbox"
                          checked={payToDriver}
                          onChange={(e) => setPayToDriver(e.target.checked)}
                          style={{ width: '16px', height: '16px', accentColor: '#f59e0b', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '13px', fontWeight: '600', color: payToDriver ? '#b45309' : '#64748b' }}>
                          Pay to driver on delivery
                        </span>
                      </label>
                    )}
                  </div>

                  {payToDriver && deliveryFee > 0 && (
                    <div style={{
                      marginTop: '10px',
                      padding: '10px 14px',
                      borderRadius: '10px',
                      background: '#fffbeb',
                      border: '1px solid #fcd34d',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '13px',
                      color: '#92400e'
                    }}>
                      <span style={{ fontSize: '16px' }}>🚚</span>
                      <span>
                        {deliveryFee === 1
                          ? 'Delivery fee based on customer area — paid to driver on delivery'
                          : `Customer will pay AED ${Math.round(deliveryFee)} to driver on delivery`}
                      </span>
                    </div>
                  )}

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: '15px',
                    paddingTop: '15px',
                    borderTop: '2px solid var(--gray-300)',
                    fontWeight: '700',
                    fontSize: '18px'
                  }}>
                    <span>{language === 'ar' ? 'الإجمالي' : 'Total'}</span>
                    <span style={{ color: 'var(--primary)' }}><AedPrice amount={calculateTotal()} /></span>
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary btn-lg"
                  style={{ width: '100%', marginTop: '20px' }}
                  disabled={submitting}
                >
                  {submitting
                    ? (language === 'ar' ? 'جاري الإنشاء...' : 'Creating...')
                    : (language === 'ar' ? 'إنشاء الطلب وإنشاء رابط الدفع' : 'Create Order & Generate Payment Link')
                  }
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateOrder;
